import moment from 'moment';
import { Comment } from 'types';

interface PartitionedComments {
  [key: string]: Comment[];
}

interface DateParts {
  day?: string;
  dayPeriod?: string;
  hour?: string;
  literal?: string;
  minute?: string;
  month?: string;
  timeZoneName?: string;
  weekday?: string;
  year?: string;
}

const CommentsUtils = {
  /**
   * returns only first name
   */
  getAuthorFirstName: (name: string) => name.trim().split(' ')[0],
  /**
   * returns if author is racker
   */
  isAuthorRacker: (roles: string[]) =>
    roles.reduce((acc, role) => acc || role.toLowerCase() === 'racker', false),
  /**
   * return date and time for comment in correct format
   */
  getDateAndTimeForDisplay: (
    dateStr: string,
    dateTimeSeparator: string,
    timeZone: string = 'default',
  ) => {
    const dateObj = new Date(dateStr);
    const dateParts = new Intl.DateTimeFormat(timeZone, {
      // @ts-ignore
      hourCycle: 'h12',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'long',
      year: 'numeric',
      timeZoneName: 'short',
    })
      .formatToParts(dateObj)
      // @ts-ignore
      .reduce<DateParts>((fragmentedDateObj, dateTimeFragment) => {
        fragmentedDateObj[dateTimeFragment.type] = dateTimeFragment.value;
        return fragmentedDateObj;
      }, {});
    const {
      weekday,
      day,
      dayPeriod,
      month,
      timeZoneName,
      hour,
      minute,
      year,
    } = dateParts;
    return `${weekday}, ${month} ${day}, ${year} ${dateTimeSeparator} ${hour}:${minute} ${
      dayPeriod ? dayPeriod.toLocaleUpperCase() : ''
    } ${timeZoneName}`;
  },
  /**
   * partitions comments by day
   */
  partitionCommentsByDay: (
    comments: Comment[],
    sortPartitionAscending: boolean = false,
  ) => {
    const result = comments.reduce<PartitionedComments>(
      (partition, comment) => {
        const createdDateStr = comment.created.split('T')[0];
        if (partition[createdDateStr]) {
          partition[createdDateStr].push(comment);
        } else {
          partition[createdDateStr] = [comment];
        }

        return partition;
      },
      {},
    );
    return Object.entries(result)
      .sort(([partitionDate1], [partitionDate2]) => {
        return partitionDate1 < partitionDate2 ? 1 : -1;
      })
      .map((partition) => {
        const sortOrder = sortPartitionAscending ? 1 : -1;
        return partition[1].sort(
          (comment1, comment2) =>
            new Date(comment1.created) < new Date(comment2.created)
              ? sortOrder * -1
              : sortOrder * 1,
        );
      });
  },
  /** Returns sorted and joined roles of a given roles array */
  getDisplayRoles: (roles: string[]) =>
    roles.sort(CommentsUtils.roleSort).join(', '),

  /** Returns transformed date of a given comment using moment library */
  getExtendedDate: (comment: Comment) => moment(comment.created),

  /**
   * Calculates if the current comment and the previous comment were created
   * on different days
   */
  isDifferentDay: (comment: Comment, prevComment: Comment) =>
    !prevComment || !moment(comment.created).isSame(prevComment.created, 'day'),

  /**
   * Calculates if the current comment needs a spacer after it, based on the
   * next comment
   */
  needsSpacer: (comment: Comment, nextComment: Comment) =>
    nextComment && moment(comment.created).isSame(nextComment.created, 'day'),

  /**
   * Calculates if two given strings are sorted alphabetically if not 'Primary'
   */
  roleSort: (a: string, b: string) => (a === 'Primary' || a < b ? -1 : 1),

  /** Returns boolean for if two given comments are sorted by created date */
  sortByCreated: (a: Comment, b: Comment) => (a.created < b.created ? 1 : -1),
};

export { CommentsUtils };
